import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ClientLayout from '../../components/layout/ClientLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import { 
  Shield, BookOpen, Target, Calendar, Flame, Award,
  ChevronRight, ChevronDown, ChevronUp, Check, Send,
  Loader2, AlertCircle, History, Trophy, Zap, Dumbbell,
  HelpCircle, CheckCircle2, XCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GuardianPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [progress, setProgress] = useState(null);
  const [todayLesson, setTodayLesson] = useState(null);
  const [lessonExpanded, setLessonExpanded] = useState(false);
  const [lessonCompleted, setLessonCompleted] = useState(false);
  const [showLogForm, setShowLogForm] = useState(false);
  const [submittingLog, setSubmittingLog] = useState(false);
  const [showReflection, setShowReflection] = useState(false);
  const [reflectionAnswer, setReflectionAnswer] = useState(null);
  const [dayCompleted, setDayCompleted] = useState(false);
  const [logForm, setLogForm] = useState({
    que_aplico: '',
    donde_lo_aplico: '',
    que_observo: '',
    que_corregira: '',
    comentarios: ''
  });

  // Preguntas reflexivas por semana
  const reflectionQuestions = {
    1: {
      question: "Hoy has trabajado la intención en el entrenamiento. ¿Cuál crees que es la diferencia principal entre entrenar con propósito y entrenar por inercia?",
      options: [
        { id: 'a', text: "La diferencia está en el tiempo: con propósito se tarda más", correct: false },
        { id: 'b', text: "La diferencia está en la presencia mental y la calidad de cada repetición", correct: true },
        { id: 'c', text: "No hay diferencia real, el músculo no distingue", correct: false },
        { id: 'd', text: "La diferencia es solo motivacional, no física", correct: false }
      ],
      feedback_correct: "Exacto. La presencia mental transforma cada repetición en una oportunidad de mejora. El Guardián entrena con los ojos abiertos.",
      feedback_incorrect: "Reflexiona: ¿qué cambia cuando realmente prestas atención a lo que haces? La diferencia está en la calidad de la presencia, no en el tiempo ni solo en la motivación."
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statusRes, progressRes, lessonRes] = await Promise.all([
        axios.get(`${API}/me/guardian-status`),
        axios.get(`${API}/me/guardian-progress`),
        axios.get(`${API}/me/guardian-today-lesson`)
      ]);
      
      setStatus(statusRes.data);
      setProgress(progressRes.data);
      setTodayLesson(lessonRes.data);
      
      if (!statusRes.data.has_access) {
        navigate('/client');
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 403) {
        navigate('/client');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteLesson = async () => {
    try {
      await axios.post(`${API}/me/guardian-complete-lesson/${todayLesson.lesson.id}`);
      setLessonCompleted(true);
      toast.success('Lección marcada como vista. Ahora ve a entrenar.');
    } catch (error) {
      toast.error('Error al completar lección');
    }
  };

  const handleSubmitLog = async () => {
    if (!logForm.que_aplico || !logForm.que_observo) {
      toast.error('Completa los campos obligatorios');
      return;
    }

    setSubmittingLog(true);
    try {
      const response = await axios.post(`${API}/me/guardian-daily-log`, {
        microlesson_id: todayLesson.lesson.id,
        ...logForm
      });
      
      toast.success('Registro guardado');
      setShowLogForm(false);
      setShowReflection(true); // Mostrar pregunta reflexiva
      
      if (response.data.pilot_completed) {
        toast.success('¡Has completado el piloto! Ya puedes hacer la evaluación final.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar registro');
    } finally {
      setSubmittingLog(false);
    }
  };

  const handleReflectionAnswer = (optionId) => {
    setReflectionAnswer(optionId);
  };

  const handleCompleteDay = () => {
    setDayCompleted(true);
    setShowReflection(false);
    toast.success('¡Día completado! Buen trabajo, Aspirante.');
    fetchData();
  };

  if (loading) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
        </div>
      </ClientLayout>
    );
  }

  const pilot = progress?.pilot || {};
  const progressPercent = pilot.progress_percent || 0;
  const currentWeek = progress?.current_week || 1;
  const currentReflection = reflectionQuestions[currentWeek] || reflectionQuestions[1];

  return (
    <ClientLayout>
      <div className="space-y-6 animate-fade-in pb-20" data-testid="guardian-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-full bg-amber-500/20">
            <Shield className="h-8 w-8 text-amber-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Objetivo Guardián</h1>
            <p className="text-muted-foreground">Nivel: <span className="text-amber-400 font-medium">ASPIRANTE</span></p>
          </div>
        </div>

        {/* Progress Card */}
        <Card className="bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-transparent border-amber-500/30">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-muted-foreground">Piloto · 5 Semanas</p>
                <p className="text-2xl font-bold text-foreground">
                  Semana {progress?.current_week || 1} · Día {progress?.current_day || 1}
                </p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-amber-400">{progressPercent}%</p>
                <p className="text-xs text-muted-foreground">completado</p>
              </div>
            </div>
            <Progress value={progressPercent} className="h-3 bg-amber-950" />
            
            <div className="grid grid-cols-3 gap-4 mt-6">
              <div className="text-center p-3 rounded-lg bg-black/20">
                <BookOpen className="h-5 w-5 text-amber-400 mx-auto mb-1" />
                <p className="text-lg font-bold text-foreground">{pilot.lessons_completed || 0}</p>
                <p className="text-xs text-muted-foreground">Lecciones</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-black/20">
                <Target className="h-5 w-5 text-amber-400 mx-auto mb-1" />
                <p className="text-lg font-bold text-foreground">{pilot.logs_completed || 0}</p>
                <p className="text-xs text-muted-foreground">Registros</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-black/20">
                <Flame className="h-5 w-5 text-orange-400 mx-auto mb-1" />
                <p className="text-lg font-bold text-foreground">{progress?.streak || 0}</p>
                <p className="text-xs text-muted-foreground">Racha</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Mensaje motivacional */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Zap className="h-5 w-5 text-amber-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-foreground">
                <strong className="text-amber-300">Hoy no vas a entrenar por inercia.</strong>{' '}
                Hoy vas a aplicar una idea concreta de tu formación.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Today's Lesson */}
        {todayLesson && (
          <Card className="bg-zinc-900/50 border-zinc-800" data-testid="today-lesson-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-amber-400" />
                  Microlección del Día
                </CardTitle>
                {todayLesson.already_completed && (
                  <span className="px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400 flex items-center gap-1">
                    <Check className="h-3 w-3" /> Completada
                  </span>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div 
                className="p-4 rounded-lg bg-black/30 cursor-pointer"
                onClick={() => setLessonExpanded(!lessonExpanded)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-amber-400 font-medium">
                      Semana {todayLesson.week} · Día {todayLesson.day}
                    </p>
                    <h3 className="text-lg font-bold text-foreground mt-1">
                      {todayLesson.lesson.titulo}
                    </h3>
                    {!lessonExpanded && (
                      <p className="text-sm text-muted-foreground mt-2">
                        {todayLesson.lesson.objetivo}
                      </p>
                    )}
                  </div>
                  <Button variant="ghost" size="icon">
                    {lessonExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                  </Button>
                </div>

                {lessonExpanded && (
                  <div className="mt-4 space-y-4 pt-4 border-t border-zinc-700 animate-fade-in">
                    <div>
                      <p className="text-xs text-amber-400 font-medium mb-1">OBJETIVO</p>
                      <p className="text-sm text-foreground">{todayLesson.lesson.objetivo}</p>
                    </div>
                    
                    <div>
                      <p className="text-xs text-amber-400 font-medium mb-1">IDEA CLAVE</p>
                      <p className="text-sm text-foreground font-medium">{todayLesson.lesson.idea_clave}</p>
                    </div>
                    
                    <div>
                      <p className="text-xs text-amber-400 font-medium mb-1">EXPLICACIÓN</p>
                      <p className="text-sm text-foreground/90">{todayLesson.lesson.explicacion}</p>
                    </div>
                    
                    <div>
                      <p className="text-xs text-amber-400 font-medium mb-1">EJEMPLO</p>
                      <p className="text-sm text-foreground/90">{todayLesson.lesson.ejemplo}</p>
                    </div>
                    
                    <div className="p-3 rounded bg-red-500/10 border border-red-500/20">
                      <p className="text-xs text-red-400 font-medium mb-1">ERROR FRECUENTE</p>
                      <p className="text-sm text-foreground/90">{todayLesson.lesson.error_frecuente}</p>
                    </div>
                    
                    <div className="p-3 rounded bg-amber-500/10 border border-amber-500/20">
                      <p className="text-xs text-amber-400 font-medium mb-1">ACCIÓN PRÁCTICA PARA HOY</p>
                      <p className="text-sm text-foreground">{todayLesson.lesson.accion_practica}</p>
                    </div>
                    
                    <div>
                      <p className="text-xs text-amber-400 font-medium mb-1">COMPROBACIÓN</p>
                      <p className="text-sm text-foreground/90 italic">{todayLesson.lesson.comprobacion}</p>
                    </div>
                  </div>
                )}
              </div>

              {!todayLesson.already_completed && !lessonCompleted && (
                <Button 
                  className="w-full bg-amber-600 hover:bg-amber-700"
                  onClick={handleCompleteLesson}
                >
                  <Check className="h-4 w-4 mr-2" />
                  Marcar lección como vista
                </Button>
              )}

              {/* Paso 2: Ir a entrenar */}
              {(lessonCompleted || todayLesson.already_completed) && !showLogForm && !todayLesson.log_id && !showReflection && (
                <div className="space-y-3">
                  <Card className="bg-gradient-to-r from-primary/10 to-transparent border-primary/30">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Zap className="h-6 w-6 text-primary" />
                        <div className="flex-1">
                          <p className="font-medium text-foreground">Ahora toca entrenar</p>
                          <p className="text-sm text-muted-foreground">
                            Aplica la acción práctica durante tu sesión
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Button 
                    className="w-full"
                    variant="outline"
                    onClick={() => navigate('/portal/session')}
                  >
                    <Dumbbell className="h-4 w-4 mr-2" />
                    Ir a mi sesión de entrenamiento
                  </Button>
                  
                  <Button 
                    className="w-full bg-amber-600 hover:bg-amber-700"
                    onClick={() => setShowLogForm(true)}
                  >
                    <Target className="h-4 w-4 mr-2" />
                    Ya entrené · Registrar aplicación
                  </Button>
                </div>
              )}

              {todayLesson.log_id && !showReflection && !dayCompleted && (
                <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                  <div className="flex items-center gap-2 text-green-400">
                    <Check className="h-5 w-5" />
                    <span className="font-medium">Registro del día completado</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Log Form */}
        {showLogForm && (
          <Card className="bg-zinc-900/50 border-amber-500/30" data-testid="log-form-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Target className="h-5 w-5 text-amber-400" />
                Registro Post-Entrenamiento
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Registra qué aplicaste hoy de la microlección durante tu entrenamiento.
              </p>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>¿Qué aplicaste? *</Label>
                  <Textarea
                    value={logForm.que_aplico}
                    onChange={(e) => setLogForm({...logForm, que_aplico: e.target.value})}
                    placeholder="Describe qué elemento de la lección aplicaste..."
                    className="bg-zinc-800/50 border-zinc-700 min-h-[80px]"
                  />
                </div>

                <div className="space-y-2">
                  <Label>¿Dónde lo aplicaste?</Label>
                  <Textarea
                    value={logForm.donde_lo_aplico}
                    onChange={(e) => setLogForm({...logForm, donde_lo_aplico: e.target.value})}
                    placeholder="En qué ejercicio o momento..."
                    className="bg-zinc-800/50 border-zinc-700"
                  />
                </div>

                <div className="space-y-2">
                  <Label>¿Qué observaste? *</Label>
                  <Textarea
                    value={logForm.que_observo}
                    onChange={(e) => setLogForm({...logForm, que_observo: e.target.value})}
                    placeholder="¿Qué notaste al aplicarlo?"
                    className="bg-zinc-800/50 border-zinc-700 min-h-[80px]"
                  />
                </div>

                <div className="space-y-2">
                  <Label>¿Qué corregirás?</Label>
                  <Textarea
                    value={logForm.que_corregira}
                    onChange={(e) => setLogForm({...logForm, que_corregira: e.target.value})}
                    placeholder="¿Qué ajustarás para la próxima vez?"
                    className="bg-zinc-800/50 border-zinc-700"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Comentarios adicionales</Label>
                  <Textarea
                    value={logForm.comentarios}
                    onChange={(e) => setLogForm({...logForm, comentarios: e.target.value})}
                    placeholder="Cualquier otra observación..."
                    className="bg-zinc-800/50 border-zinc-700"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setShowLogForm(false)}
                >
                  Cancelar
                </Button>
                <Button 
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                  onClick={handleSubmitLog}
                  disabled={submittingLog}
                >
                  {submittingLog ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Guardar registro
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pregunta Reflexiva */}
        {showReflection && (
          <Card className="bg-gradient-to-br from-purple-500/10 via-indigo-500/5 to-transparent border-purple-500/30" data-testid="reflection-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2 text-purple-300">
                <HelpCircle className="h-5 w-5" />
                Reflexión del Día
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-foreground font-medium">
                {currentReflection.question}
              </p>

              <div className="space-y-2">
                {currentReflection.options.map((option) => (
                  <div
                    key={option.id}
                    onClick={() => !reflectionAnswer && handleReflectionAnswer(option.id)}
                    className={`p-4 rounded-lg border cursor-pointer transition-all ${
                      reflectionAnswer === option.id
                        ? option.correct
                          ? 'border-green-500 bg-green-500/10'
                          : 'border-red-500 bg-red-500/10'
                        : reflectionAnswer && option.correct
                          ? 'border-green-500 bg-green-500/10'
                          : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600'
                    } ${reflectionAnswer ? 'cursor-default' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className={`font-bold ${
                        reflectionAnswer === option.id
                          ? option.correct ? 'text-green-400' : 'text-red-400'
                          : 'text-muted-foreground'
                      }`}>
                        {option.id.toUpperCase()})
                      </span>
                      <span className="text-foreground">{option.text}</span>
                      {reflectionAnswer && option.correct && (
                        <CheckCircle2 className="h-5 w-5 text-green-400 ml-auto flex-shrink-0" />
                      )}
                      {reflectionAnswer === option.id && !option.correct && (
                        <XCircle className="h-5 w-5 text-red-400 ml-auto flex-shrink-0" />
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {reflectionAnswer && (
                <div className={`p-4 rounded-lg ${
                  currentReflection.options.find(o => o.id === reflectionAnswer)?.correct
                    ? 'bg-green-500/10 border border-green-500/30'
                    : 'bg-amber-500/10 border border-amber-500/30'
                }`}>
                  <p className="text-sm text-foreground">
                    {currentReflection.options.find(o => o.id === reflectionAnswer)?.correct
                      ? currentReflection.feedback_correct
                      : currentReflection.feedback_incorrect
                    }
                  </p>
                </div>
              )}

              {reflectionAnswer && (
                <Button 
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  onClick={handleCompleteDay}
                >
                  <Check className="h-4 w-4 mr-2" />
                  Completar día
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Día completado */}
        {dayCompleted && (
          <Card className="bg-gradient-to-br from-green-500/10 to-transparent border-green-500/30">
            <CardContent className="p-6 text-center">
              <div className="p-4 rounded-full bg-green-500/20 w-fit mx-auto mb-4">
                <Trophy className="h-10 w-10 text-green-400" />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">¡Día completado!</h3>
              <p className="text-muted-foreground">
                Has completado todas las tareas del día. Descansa y vuelve mañana.
              </p>
            </CardContent>
          </Card>
        )}

        {/* History Link */}
        {!showReflection && !dayCompleted && (
          <Card 
            className="bg-zinc-900/50 border-zinc-800 cursor-pointer hover:border-zinc-700 transition-all"
            onClick={() => navigate('/client/guardian/history')}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <History className="h-5 w-5 text-muted-foreground" />
                  <span className="text-foreground">Ver historial de registros</span>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Evaluation Card (if pilot completed) */}
        {pilot.completed && (
          <Card className="bg-gradient-to-br from-green-500/10 to-transparent border-green-500/30">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-green-500/20">
                  <Trophy className="h-8 w-8 text-green-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-foreground">¡Piloto completado!</h3>
                  <p className="text-sm text-muted-foreground">
                    Ya puedes realizar la evaluación final para avanzar al siguiente nivel.
                  </p>
                </div>
              </div>
              <Button 
                className="w-full mt-4 bg-green-600 hover:bg-green-700"
                onClick={() => navigate('/client/guardian/evaluation')}
              >
                <Award className="h-4 w-4 mr-2" />
                Realizar evaluación final
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </ClientLayout>
  );
};

export default GuardianPage;
